export const SESSION_SIZE = 10;

type Categorised = { category?: string };

/**
 * Order the feed by reader preference without destroying variety.
 *
 * Each card keeps its original position but is pulled forward in proportion to
 * the weight of its category (explicit interests weigh most; learned affinity
 * from reactions/saves adds a lighter nudge). A bounded boost means strongly
 * preferred cards surface first while the round still mixes topics, we never
 * hard-cluster one category. Pure + stable, so it's deterministic and testable.
 */
export function personalizeFeed<T extends Categorised>(
  cards: T[],
  weights: Record<string, number>,
  boost = 3,
): T[] {
  if (cards.length <= 1) return cards;
  const maxWeight = Math.max(0, ...Object.values(weights));
  if (maxWeight <= 0) return cards;
  return cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const wa = weights[a.card.category ?? ""] ?? 0;
      const wb = weights[b.card.category ?? ""] ?? 0;
      const rankA = a.index - wa * boost;
      const rankB = b.index - wb * boost;
      return rankA - rankB || a.index - b.index;
    })
    .map((entry) => entry.card);
}

/**
 * Build category weights from explicit interests (strong) plus learned affinity
 * (capped, lighter) so a single session can't drown out the user's stated picks.
 */
export function readerWeights(
  interests: string[],
  affinity: Record<string, number>,
  { interestWeight = 2, affinityCap = 3, affinityScale = 0.5 } = {},
): Record<string, number> {
  const weights: Record<string, number> = {};
  for (const interest of interests) {
    weights[interest] = (weights[interest] ?? 0) + interestWeight;
  }
  for (const [category, count] of Object.entries(affinity)) {
    const learned = Math.min(Math.max(count, 0), affinityCap) * affinityScale;
    weights[category] = (weights[category] ?? 0) + learned;
  }
  return weights;
}

/**
 * Resume position: the index of the first card the reader hasn't seen yet.
 * Lets a returning reader pick up where they left off (card 6, not card 1) and
 * skips cards already read across refreshes. Returns 0 when everything has been
 * seen (caught up, start from the top again) or the feed is empty.
 */
export function resumeIndex(orderedIds: string[], seen: Iterable<string>): number {
  const seenSet = seen instanceof Set ? seen : new Set(seen);
  const idx = orderedIds.findIndex((id) => !seenSet.has(id));
  return idx === -1 ? 0 : idx;
}

/**
 * Seed the round-scoped "viewed" list when resuming mid-round, so the
 * after-10th analysis still fires at the right moment. Returns the already-seen
 * card ids that fall in the resume card's round, plus the resume card itself.
 */
export function seedViewedForResume(
  orderedIds: string[],
  startIdx: number,
  seen: Iterable<string>,
  size = SESSION_SIZE,
): string[] {
  const seenSet = seen instanceof Set ? seen : new Set(seen);
  const roundStart = roundStartForIndex(startIdx, size);
  const roundIds = orderedIds.slice(roundStart, roundStart + size);
  const seededFromRound = roundIds.filter((id, i) => seenSet.has(id) || roundStart + i === startIdx);
  const startId = orderedIds[startIdx];
  return Array.from(new Set(startId ? [...seededFromRound, startId] : seededFromRound));
}

export function roundStartForIndex(index: number, size = SESSION_SIZE): number {
  if (!Number.isFinite(index) || index <= 0) return 0;
  return Math.floor(index / size) * size;
}

export function roundEndForStart(total: number, start: number, size = SESSION_SIZE): number {
  return Math.min(Math.max(total, 0), start + size);
}

export function roundSizeForStart(total: number, start: number, size = SESSION_SIZE): number {
  return Math.max(0, roundEndForStart(total, start, size) - start);
}

export function nextRoundStartIndex(
  activeIndex: number,
  total: number,
  size = SESSION_SIZE
): number | null {
  if (total <= 0) return null;
  const nextStart = roundStartForIndex(activeIndex, size) + size;
  return nextStart < total ? nextStart : null;
}
